import { ChatActionButton } from "@/components/ui/chat-action-button";
import committedEditIcon from "@/assets/ai-chat-actions/edit-calories.svg";
import committedThumbDownIcon from "@/assets/ai-chat-actions/thumb-down.svg";
import committedThumbUpIcon from "@/assets/ai-chat-actions/thumb-up.svg";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { Clipboard } from "@capacitor/clipboard";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import type {
  ChatMessageImage,
  ChatMessageInterrupt,
  ChatMessagePlanFoodsCommitted,
  ChatMessagePresentation,
  ChatMessageRole,
} from "../hooks/use-chat-session";
import { useSubmitChatFeedback } from "../hooks/use-submit-chat-feedback";
import type { ChatMessageActionMode } from "../utils/chat-message-actions";
import {
  CHAT_THINKING_PULSE_ANIMATION,
  CHAT_THINKING_PULSE_KEYFRAMES,
} from "./chat-thinking-pulse";
import { useTypewriter } from "../hooks/use-typewriter";
import { PlanFoodsCommittedCard } from "./plan-foods-committed-card";

type Vote = "up" | "down" | null;

const ThumbsUpIcon = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden
    className="h-[22px] w-[22px]"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 10.5V20H4.5C3.67 20 3 19.33 3 18.5V12C3 11.17 3.67 10.5 4.5 10.5H7ZM7 10.5L11 3.5C12.1 3.5 13 4.4 13 5.5V9.5H18.5C19.6 9.5 20.5 10.4 20.5 11.5L19 18.5C18.85 19.4 18.1 20 17.2 20H7V10.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      strokeLinecap="round"
      fill={filled ? "currentColor" : "none"}
    />
  </svg>
);

const ThumbsDownIcon = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden
    className="h-[22px] w-[22px]"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 13.5V4H19.5C20.33 4 21 4.67 21 5.5V12C21 12.83 20.33 13.5 19.5 13.5H17ZM17 13.5L13 20.5C11.9 20.5 11 19.6 11 18.5V14.5H5.5C4.4 14.5 3.5 13.6 3.5 12.5L5 5.5C5.15 4.6 5.9 4 6.8 4H17V13.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      strokeLinecap="round"
      fill={filled ? "currentColor" : "none"}
    />
  </svg>
);

const CommittedThumbIcon = ({ src }: { src: string }) => (
  <img src={src} alt="" aria-hidden className="size-11 shrink-0" />
);

const RetryIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className="h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 11a8 8 0 1 0-.78 4M20 5v6h-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PencilIcon = () => (
  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.3103 4.62915L14.1216 0.439461C13.9823 0.300137 13.8169 0.189617 13.6349 0.114213C13.4529 0.03881 13.2578 0 13.0608 0C12.8638 0 12.6687 0.03881 12.4867 0.114213C12.3047 0.189617 12.1393 0.300137 12 0.439461L0.439695 12.0007C0.299801 12.1395 0.188889 12.3047 0.113407 12.4867C0.0379245 12.6688 -0.000621974 12.864 7.58902e-06 13.061V17.2507C7.58902e-06 17.6485 0.158043 18.0301 0.439347 18.3114C0.720652 18.5927 1.10218 18.7507 1.50001 18.7507H5.6897C5.88675 18.7513 6.08197 18.7128 6.26399 18.6373C6.44602 18.5618 6.61122 18.4509 6.75001 18.311L18.3103 6.75071C18.4496 6.61142 18.5602 6.44604 18.6356 6.26403C18.711 6.08202 18.7498 5.88694 18.7498 5.68993C18.7498 5.49292 18.711 5.29784 18.6356 5.11582C18.5602 4.93381 18.4496 4.76844 18.3103 4.62915ZM5.6897 17.2507H1.50001V13.061L9.75001 4.81102L13.9397 9.00071L5.6897 17.2507ZM15 7.93946L10.8103 3.75071L13.0603 1.50071L17.25 5.68946L15 7.93946Z"
      fill="currentColor"
    />
  </svg>
);

interface AgentMessageActionsProps {
  sessionId: string;
  /** Langfuse trace id for this message. Null/undefined disables the
   *  thumbs UI because feedback can't be tied to a specific turn. */
  traceId: string | null | undefined;
  committed?: boolean;
}

const AgentMessageActions = ({ sessionId, traceId, committed }: AgentMessageActionsProps) => {
  const [vote, setVote] = useState<Vote>(null);
  // Last rating actually POSTed to the backend. Prevents re-submitting the
  // same rating if the user toggles off-then-on, and avoids in-flight dupes.
  const lastSentRef = useRef<"up" | "down" | null>(null);
  const submitFeedback = useSubmitChatFeedback();

  const canRate = Boolean(traceId) && Boolean(sessionId);
  const isPending = submitFeedback.isPending;

  const handleClick = (next: Exclude<Vote, null>) => () => {
    if (!canRate || isPending) return;
    const newVote: Vote = vote === next ? null : next;
    setVote(newVote);
    // Only POST when the rating is a definite up/down AND differs from
    // whatever we last sent. There is no API to clear a rating, so toggling
    // off (newVote === null) is purely a UI state change — see the doc note
    // about no server-side dedupe.
    if (newVote !== null && newVote !== lastSentRef.current) {
      lastSentRef.current = newVote;
      submitFeedback.mutate({
        session_id: sessionId,
        trace_id: traceId as string,
        rating: newVote,
      });
    }
  };

  const buttonClasses = (active: boolean) =>
    cn(
      "flex h-8 w-8 items-center justify-center rounded-full",
      committed && "h-11 w-11",
      "transition-colors active:bg-white/10",
      active
        ? "text-[var(--color-os-brand)]"
        : committed
          ? "text-white"
          : "text-white/60 hover:text-white",
      !canRate && "cursor-not-allowed opacity-40 hover:text-white/60",
      committed && active && "bg-white/10",
    );

  return (
    <div className={cn("mt-1 -ml-1.5 flex items-center gap-1", committed && "mt-0 ml-0 gap-0.5")}>
      <button
        type="button"
        aria-label="Helpful"
        aria-pressed={vote === "up"}
        disabled={!canRate || isPending}
        onClick={handleClick("up")}
        className={buttonClasses(vote === "up")}
      >
        {committed ? (
          <CommittedThumbIcon src={committedThumbUpIcon} />
        ) : (
          <ThumbsUpIcon filled={vote === "up"} />
        )}
      </button>
      <button
        type="button"
        aria-label="Not helpful"
        aria-pressed={vote === "down"}
        disabled={!canRate || isPending}
        onClick={handleClick("down")}
        className={buttonClasses(vote === "down")}
      >
        {committed ? (
          <CommittedThumbIcon src={committedThumbDownIcon} />
        ) : (
          <ThumbsDownIcon filled={vote === "down"} />
        )}
      </button>
    </div>
  );
};

interface ChatMessageBubbleProps {
  role: ChatMessageRole;
  text: string;
  images?: ChatMessageImage[];
  interrupt?: ChatMessageInterrupt;
  planFoodsCommitted?: ChatMessagePlanFoodsCommitted;
  /** Selects the one action treatment owned by this message. Committed turns
   * anchor their single action row to the last deterministic card. */
  actionMode?: ChatMessageActionMode;
  /** Adds a visual boundary before ordinary assistant copy that follows a
   * deterministic committed-food card in the same compound response. */
  separateFromFollowingMessage?: boolean;
  showInterruptActions?: boolean;
  onInterruptAnswer?: (answer: "yes" | "no") => void;
  /** Set when this message is the one currently streaming, so we suppress the
   *  thumb action row until the agent has finished replying. */
  isStreaming?: boolean;
  /** Active chat session id; required to post thumb feedback. */
  sessionId?: string;
  /** Langfuse trace id for this message; null disables the thumbs UI. */
  traceId?: string | null;
  /** Re-run the failed turn. Wired only for the last error message. */
  onRetry?: () => void;
  /** Show the retry button under an error bubble. */
  showRetry?: boolean;
  /** Fake-stream this (locally-seeded) message in with the typewriter cadence,
   *  matching how real agent answers reveal. See `streamInMessageIdAtom`. */
  streamIn?: boolean;
  /** Called once the fake stream finishes, so the owner can clear `streamIn`
   *  and the message never re-animates on a later re-render. */
  onStreamInDone?: () => void;
  /** Called when the user taps "Edit Calories" — focuses the input bar. */
  onEdit?: () => void;
  /** Display treatment selected by machine-readable agent frame metadata. */
  presentation?: ChatMessagePresentation;
  /** Transcript message id, exposed as `data-message-id` so the page can
   *  scroll to a specific message (My Plan "just added" badge). */
  messageId?: string;
}

const useLongPressCopy = (getText: () => string) => {
  const { showInfo, showError } = useToast();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPressed, setIsPressed] = useState(false);

  const start = useCallback(() => {
    setIsPressed(true);
    const text = getText();
    if (!text) return;
    timerRef.current = setTimeout(() => {
      const copy = async () => {
        try {
          await Clipboard.write({ string: text });
        } catch {
          const webClipboard = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
          if (!webClipboard || typeof webClipboard.writeText !== "function") throw new Error();
          await webClipboard.writeText(text);
        }
      };

      void copy()
        .then(() => {
          showInfo("Message copied to clipboard.");
        })
        .catch(() => {
          showError("Unable to copy this message.");
        });
    }, 500);
  }, [getText, showInfo, showError]);

  const cancel = useCallback(() => {
    setIsPressed(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const preventContextMenu = useCallback((e: React.MouseEvent) => e.preventDefault(), []);

  return {
    isPressed,
    handlers: {
      onPointerDown: start,
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onContextMenu: preventContextMenu,
    },
  };
};

const splitBackgroundNote = (text: string): { title: string; body: string } => {
  const boundary = text.search(/\r?\n[\t ]*\r?\n/);
  if (boundary === -1) return { title: text.trim(), body: "" };
  const separator = text.slice(boundary).match(/^\r?\n[\t ]*\r?\n/)?.[0] ?? "";
  return {
    title: text.slice(0, boundary).trim(),
    body: text.slice(boundary + separator.length).trim(),
  };
};

const MessageMarkdown = ({ text, compact = false }: { text: string; compact?: boolean }) => (
  <div
    className={cn(
      "flex flex-col gap-2 break-words",
      "[&_a]:underline",
      "[&_strong]:font-semibold",
      "[&_em]:italic",
      "[&_ul]:flex [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-1 [&_ul]:pl-5",
      "[&_ol]:flex [&_ol]:list-decimal [&_ol]:flex-col [&_ol]:gap-1 [&_ol]:pl-5",
      "[&_li]:marker:text-white/60",
      "[&_code]:rounded [&_code]:bg-white/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em]",
      "[&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-white/10 [&_pre]:p-3",
      "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
      "[&_blockquote]:border-l-2 [&_blockquote]:border-white/30 [&_blockquote]:pl-3 [&_blockquote]:opacity-90",
      "[&_h1]:text-lg [&_h1]:font-semibold",
      "[&_h2]:text-base [&_h2]:font-semibold",
      "[&_h3]:font-semibold",
    )}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        a: ({ children, href, title }) => (
          <a href={href} title={title} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
        p: ({ children }) =>
          compact ? <span>{children}</span> : <p className="mb-3 last:mb-0">{children}</p>,
      }}
    >
      {text}
    </ReactMarkdown>
  </div>
);

export const ChatMessageBubble = memo(function ChatMessageBubble({
  role,
  text,
  images,
  interrupt,
  planFoodsCommitted,
  actionMode = "standard",
  separateFromFollowingMessage,
  showInterruptActions,
  onInterruptAnswer,
  isStreaming,
  sessionId,
  traceId,
  onRetry,
  showRetry,
  streamIn,
  onStreamInDone,
  messageId,
  onEdit,
  presentation,
}: ChatMessageBubbleProps) {
  const isUser = role === "user";
  const isAgent = role === "agent";
  const isError = role === "error";

  // Locally-seeded messages (e.g. the meal-coach greeting) arrive with their
  // full text at once; reveal it at the streaming cadence so it reads like a
  // real answer. Everything else passes through untouched (enabled=false).
  const { text: displayText, isDone: streamInDone } = useTypewriter(text, !!streamIn);
  useEffect(() => {
    if (streamIn && streamInDone) onStreamInDone?.();
  }, [streamIn, streamInDone, onStreamInDone]);

  const hasText = displayText.length > 0;
  const isRevealing = !!streamIn && !streamInDone;
  const parsedBackgroundNote =
    presentation === "background-note" ? splitBackgroundNote(displayText) : null;
  const backgroundNote = parsedBackgroundNote;
  const showStandardActions =
    actionMode === "standard" &&
    isAgent &&
    (hasText || Boolean(planFoodsCommitted)) &&
    Boolean(traceId) &&
    !isStreaming &&
    !isRevealing &&
    !interrupt &&
    !backgroundNote;
  const showCommittedActions =
    actionMode === "committed" &&
    isAgent &&
    Boolean(planFoodsCommitted) &&
    !isRevealing &&
    !interrupt;
  const committedActions = showCommittedActions ? (
    <div
      data-presentation="plan-foods-committed-actions"
      className="-ml-[11px] flex h-11 items-center gap-1.5"
    >
      <AgentMessageActions sessionId={sessionId ?? ""} traceId={traceId} committed />
      <button
        type="button"
        aria-label="Edit calories"
        onClick={() => onEdit?.()}
        className={cn(
          "flex h-11 shrink-0 items-center gap-[5px] pl-[13px] text-white active:text-white/70",
          textVariants.footnote,
        )}
      >
        <img
          src={committedEditIcon}
          alt=""
          aria-hidden
          className="size-[18.75px] shrink-0"
        />
        <span className="whitespace-nowrap">Edit calories</span>
      </button>
    </div>
  ) : undefined;

  const getDisplayText = useCallback(() => displayText, [displayText]);
  const { isPressed, handlers: longPressHandlers } = useLongPressCopy(getDisplayText);

  if (isAgent) {
    return (
      <div
        data-message-id={messageId}
        className={cn(
          "flex w-full justify-start",
          planFoodsCommitted && separateFromFollowingMessage && "pb-6",
        )}
      >
        <div
          className={cn(
            "flex w-full flex-col text-white",
            planFoodsCommitted ? "gap-6" : "gap-2",
            textVariants.body,
            "leading-[24px]",
          )}
        >
          <div
            {...(hasText ? longPressHandlers : {})}
            className={cn(
              "flex flex-col gap-2",
              "transition-opacity duration-75",
              isPressed && "opacity-60",
            )}
          >
            {images?.map((image, index) => (
              <img
                key={`${image.dataUrl.slice(-24)}-${index}`}
                src={image.dataUrl}
                alt={`Attached image ${index + 1}`}
                className="max-h-80 max-w-full rounded-xl object-cover"
              />
            ))}
            {planFoodsCommitted && (
              <PlanFoodsCommittedCard committed={planFoodsCommitted} actions={committedActions} />
            )}
            {hasText && backgroundNote ? (
              <div data-presentation="background-note" className="flex flex-col gap-3">
                {isStreaming && <style>{CHAT_THINKING_PULSE_KEYFRAMES}</style>}
                <div className="flex items-start gap-3 font-semibold">
                  <span
                    aria-hidden
                    data-testid="background-note-marker"
                    className="mt-1.5 size-3 shrink-0 rounded-full bg-[var(--color-os-brand)]"
                    style={isStreaming ? { animation: CHAT_THINKING_PULSE_ANIMATION } : undefined}
                  />
                  <MessageMarkdown text={backgroundNote.title} compact />
                </div>
                {backgroundNote.body && <MessageMarkdown text={backgroundNote.body} />}
                {isStreaming && (
                  <span
                    className="sr-only"
                    role="status"
                    aria-live="polite"
                    aria-label="Assistant is thinking"
                  >
                    Assistant is thinking
                  </span>
                )}
              </div>
            ) : (
              hasText && <MessageMarkdown text={displayText} />
            )}
            {interrupt && showInterruptActions && (
              <div className="mt-1 flex flex-col gap-2">
                {/* DESIGN PLACEHOLDER — layout is unstyled pending the Figma for
                 *  the editable HITL card. The gating below is correct and final:
                 *  `interrupt.editable` is the backend's signal, and it is always
                 *  false for a multi-action pause (a typed calorie correction
                 *  couldn't say which meal it applies to). Restyle freely; do not
                 *  change the condition, and do not infer editability from the
                 *  question text. */}
                {interrupt.editable && (
                  <button
                    type="button"
                    aria-label="Edit calories"
                    onClick={() => onEdit?.()}
                    className={cn(
                      "my-2 flex items-center gap-2 self-start",
                      textVariants.bodySmall,
                      "text-white/70",
                    )}
                  >
                    <PencilIcon />
                    Edit Calories
                  </button>
                )}
                <div className="flex gap-2">
                  <ChatActionButton
                    variant="solid"
                    onClick={() => onInterruptAnswer?.("yes")}
                    className="flex-1"
                  >
                    Yes, do it
                  </ChatActionButton>
                  <ChatActionButton
                    variant="outline"
                    onClick={() => onInterruptAnswer?.("no")}
                    className="flex-1"
                  >
                    No, cancel
                  </ChatActionButton>
                </div>
              </div>
            )}
          </div>
          {showStandardActions && (
            <AgentMessageActions sessionId={sessionId ?? ""} traceId={traceId} />
          )}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div data-message-id={messageId} className="flex w-full flex-col items-start gap-1.5">
        <div
          className={cn(
            "max-w-[260px] rounded-[18px] px-3 py-2",
            textVariants.body,
            "bg-red-500/20 leading-[24px] whitespace-pre-wrap text-red-200",
          )}
        >
          {hasText && <span>{displayText}</span>}
        </div>
        {showRetry && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={cn(
              "flex items-center gap-1.5 rounded-full border border-white/30 px-3 py-1.5 text-white",
              "active:bg-white/10",
              textVariants.footnote,
            )}
          >
            <RetryIcon />
            Try again
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-message-id={messageId}
      className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}
    >
      <div
        {...(hasText ? longPressHandlers : {})}
        className={cn(
          "flex max-w-[260px] flex-col gap-2 rounded-[18px] px-3 py-2",
          textVariants.body,
          "leading-[24px]",
          "rounded-br-[3px] whitespace-pre-wrap text-white",
          "transition-colors duration-75",
          isPressed ? "bg-[#1f1940]" : "bg-[#261e4d]",
        )}
      >
        {images?.map((image, index) => (
          <img
            key={`${image.dataUrl.slice(-24)}-${index}`}
            src={image.dataUrl}
            alt={`Attached image ${index + 1}`}
            className="max-h-80 max-w-full rounded-xl object-cover"
          />
        ))}
        {hasText && <span>{displayText}</span>}
      </div>
    </div>
  );
});
